import { readonly, ref } from 'vue';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export type ToastNotification = {
    id: string;
    message?: string;
    title?: string;
    subtitle?: string;
    body?: string;
    type: NotificationType;
    icon?: string;
    timeout?: number;
    closeable?: boolean;
    actionText?: string;
    actionCallback?: () => void;
    timestamp: Date;
    isSeen?: boolean;
};

export type NotifyOptions = {
    message?: string;
    title?: string;
    subtitle?: string;
    body?: string;
    type?: NotificationType;
    icon?: string;
    timeout?: number;
    closeable?: boolean;
    actionText?: string;
    actionCallback?: () => void;
    addToHistory?: boolean;
};

// Global reactive state (singleton pattern)
const notifications = ref<ToastNotification[]>([]);
const notificationHistory = ref<ToastNotification[]>([]);
let notificationIdCounter = 0;

// Default icons for each notification type
const typeIcons: Record<NotificationType, string> = {
    success: 'mdi-check-circle',
    error: 'mdi-alert-circle',
    warning: 'mdi-alert',
    info: 'mdi-information',
};

// Default timeouts for each notification type (in ms)
const typeTimeouts: Record<NotificationType, number> = {
    success: 5000,
    error: 7000,
    warning: 6000,
    info: 5000,
};

/**
 * Global notification/toast system
 * Provides a clean API to show notifications throughout the app
 */
export const useNotifications = () => {
    /**
     * Show a notification
     * @param options - Notification configuration
     * @returns The notification ID
     */
    const notify = (options: NotifyOptions | string): string => {
        // Support simple string message
        const config: NotifyOptions = typeof options === 'string' ? { message: options } : options;

        const type = config.type ?? 'info';
        const icon = config.icon ?? typeIcons[type];
        const timeout = config.timeout ?? typeTimeouts[type];
        const closeable = config.closeable ?? true;

        const notification: ToastNotification = {
            id: `notification-${++notificationIdCounter}`,
            message: config.message,
            title: config.title,
            subtitle: config.subtitle,
            body: config.body,
            type,
            icon,
            timeout,
            closeable,
            actionText: config.actionText,
            actionCallback: config.actionCallback,
            timestamp: new Date(),
            isSeen: false,
        };

        // Add to active notifications
        notifications.value.push(notification);

        const shouldAddToHistory = config.addToHistory ?? true;
        if (shouldAddToHistory) {
            notificationHistory.value.unshift(notification);
        }

        // Limit history to 10 items
        if (notificationHistory.value.length > 10) {
            notificationHistory.value = notificationHistory.value.slice(0, 10);
        }

        return notification.id;
    };

    /**
     * Dismiss a specific notification
     */
    const dismiss = (id: string) => {
        const index = notifications.value.findIndex((n) => n.id === id);
        if (index !== -1) {
            notifications.value.splice(index, 1);
        }
    };

    /**
     * Dismiss all active notifications
     */
    const dismissAll = () => {
        notifications.value = [];
    };

    /**
     * Clear notification history
     */
    const clearHistory = () => {
        notificationHistory.value = [];
    };

    /**
     * Mark all notifications as seen
     */
    const markAllAsSeen = () => {
        notificationHistory.value.forEach((notification) => {
            notification.isSeen = true;
        });
    };

    // Convenience methods for common notification types
    const success = (options: NotifyOptions | string) => {
        const config: NotifyOptions = typeof options === 'string' ? { message: options } : options;
        return notify({ ...config, type: 'success' });
    };

    const error = (options: NotifyOptions | string) => {
        const config: NotifyOptions = typeof options === 'string' ? { message: options } : options;
        return notify({ ...config, type: 'error' });
    };

    const warning = (options: NotifyOptions | string) => {
        const config: NotifyOptions = typeof options === 'string' ? { message: options } : options;
        return notify({ ...config, type: 'warning' });
    };

    const info = (options: NotifyOptions | string) => {
        const config: NotifyOptions = typeof options === 'string' ? { message: options } : options;
        return notify({ ...config, type: 'info' });
    };

    return {
        // State
        notifications: readonly(notifications),
        notificationHistory: readonly(notificationHistory),

        // Methods
        notify,
        dismiss,
        dismissAll,
        clearHistory,
        markAllAsSeen,

        // Convenience methods
        success,
        error,
        warning,
        info,

        // Aliases for backward compatibility
        showSuccess: success,
        showError: error,
    };
};
